import { z } from "zod";

const breedTypeSchema = z.enum(["purebred", "mixed"]);
const providerTypeSchema = z
    .enum(["breeder", "shelter_rescue"])
    .describe(
        "Exact provider category: breeder for breeder-sourced dogs or breeder organizations; shelter_rescue for shelter or rescue dogs and organizations.",
    );
const sexSchema = z
    .enum(["male", "female"])
    .describe('Exact dog sex. The field name is "sex", not "gender".');
const sizeSchema = z
    .enum(["toy", "small", "medium", "large", "giant"])
    .describe("Exact expected adult size category.");
const coatTypeSchema = z.enum([
    "smooth",
    "short",
    "medium",
    "long",
    "wire",
    "curly",
    "double",
    "hairless",
]);
const sheddingLevelSchema = z.enum([
    "none",
    "minimal",
    "moderate",
    "heavy",
    "seasonal_blowout",
]);
const comparisonOperatorSchema = z.enum(["eq", "gte", "lte"]);
const ratingValueSchema = z.enum(["1", "2", "3", "4", "5"]);
const searchSortSchema = z
    .enum([
        "best-match",
        "nearest-first",
        "newest-listings",
        "oldest-listings",
        "most-popular",
    ])
    .describe(
        "Listing ordering. Use nearest-first, newest-listings, oldest-listings, most-popular, or best-match exactly as written.",
    );

export const searchIntentRatingFilterSchema = z
    .object({
        value: ratingValueSchema,
        operator: comparisonOperatorSchema,
    })
    .strict();

export const searchIntentFiltersSchema = z
    .object({
        breedSlugs: z
            .array(z.string())
            .describe(
                'Exact requested breed slugs, such as ["golden-retriever"]. Keep a requested breed here even when semantic qualities are also present.',
            ),
        dogName: z.string().nullable(),
        breedType: breedTypeSchema.nullable(),
        providerName: z.string().trim().min(1).nullable().optional(),
        providerType: providerTypeSchema.nullable(),
        sex: sexSchema.nullable(),
        priceMin: z.number().int().nonnegative().nullable(),
        priceMax: z.number().int().nonnegative().nullable(),
        ageMonthsMin: z
            .number()
            .int()
            .nonnegative()
            .nullable()
            .describe("Exact minimum age in months."),
        ageMonthsMax: z
            .number()
            .int()
            .nonnegative()
            .nullable()
            .describe("Exact maximum age in months."),
        size: z
            .array(sizeSchema)
            .describe(
                'Exact size categories as an array, such as ["small"]. The field name is "size", not "sizeCategory".',
            ),
        coatType: z.array(coatTypeSchema),
        sheddingLevel: z.array(sheddingLevelSchema),
        hypoallergenic: z.boolean().nullable(),
        friendlinessChildren: searchIntentRatingFilterSchema.nullable(),
        friendlinessDogs: searchIntentRatingFilterSchema.nullable(),
        friendlinessCats: searchIntentRatingFilterSchema.nullable(),
        energyLevel: searchIntentRatingFilterSchema.nullable(),
        trainability: searchIntentRatingFilterSchema.nullable(),
        healthIssuesRisk: searchIntentRatingFilterSchema.nullable(),
        sortBy: searchSortSchema.nullable(),
        coordinates: z
            .object({
                latitude: z.number().min(-90).max(90),
                longitude: z.number().min(-180).max(180),
            })
            .strict()
            .optional(),
        locationQuery: z
            .string()
            .describe(
                'Natural-language place or address to geocode, such as "Fremont California". Use null when coordinates are available or the user gave no location.',
            )
            .nullable(),
        maxDistanceMiles: z.number().positive().nullable(),
        hardRadius: z.boolean().nullable(),
    })
    .strict();

export const searchIntentSchema = z
    .object({
        filters: searchIntentFiltersSchema,
        semanticQuery: z.string().min(1).nullable(),
        noise: z.array(z.string().min(1)),
    })
    .strict();

export type SearchIntent = z.infer<typeof searchIntentSchema>;
export type SearchIntentFilters = z.infer<typeof searchIntentFiltersSchema>;
export type SearchIntentRatingFilter = z.infer<
    typeof searchIntentRatingFilterSchema
>;

export const searchIntentBreedSchema = z
    .object({
        slug: z.string().min(1),
        name: z.string().min(1),
        alternateNames: z.array(z.string().min(1)),
    })
    .strict();

export const searchIntentVocabularySchema = z
    .object({
        version: z.string().min(1),
        breeds: z.array(searchIntentBreedSchema).min(1),
    })
    .strict();

export type SearchIntentBreed = z.infer<typeof searchIntentBreedSchema>;
export type SearchIntentVocabulary = z.infer<
    typeof searchIntentVocabularySchema
>;
